import { SelectOptions } from '@spo/logic';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Select, {
  ActionMeta,
  InputActionMeta,
  MultiValue as mv,
} from 'react-select';
import { useProductNatures } from '../../../hooks/useProductNatures';
import { arrayUniqueByKey } from '../../../utils/array';
import { metaActions } from '../../../utils/metaActions';
import { selectFilterStyles } from '../common/layout';
import { concatOptions, sortOptions } from '../common/options';
import { IconOption, MultiValue } from '../custom/CustomSelectInput';
import { ProductNaturesSearchInputProps } from './ProductNaturesSearchInputProps';

export const ProductNaturesSearchInput = ({
  onChange,
  selectedProductNatureOptions = [],
  sportIds = [],
  productNaturesToHide = [],
  referential = false,
}: ProductNaturesSearchInputProps) => {
  const { t } = useTranslation();

  const [productNaturesOptions, setProductNaturesOptions] = useState<
    SelectOptions[]
  >([]);
  const [selectedProductNatures, setSelectedProductNatures] =
    useState<mv<SelectOptions> | null>(null);
  const [searchedProductNature, setSearchedProductNature] =
    useState<string>('');

  const {
    data: searchedProductNatureData,
    isError: searchedProductNatureIsError,
    isFetching: searchedProductNatureIsFetching,
  } = useProductNatures(
    sportIds,
    searchedProductNature,
    searchedProductNature.length > 0,
    referential,
  );

  const setOptionsProductNaturesAndSelectedProductNatures = (
    productNaturesListOptions: SelectOptions[],
  ) => {
    const sortedProductNaturesListOptions = sortOptions(
      [],
      productNaturesListOptions,
    );

    const uniqueNatureOptions = [
      ...arrayUniqueByKey<SelectOptions>(
        sortedProductNaturesListOptions,
        'value',
      ),
    ];
    setProductNaturesOptions(uniqueNatureOptions);

    selectedProductNatureOptions.length > 0 &&
      setSelectedProductNatures(selectedProductNatureOptions);
  };

  useEffect(() => {
    if (searchedProductNatureData && searchedProductNatureData.length > 0) {
      const productNaturesListOptions: SelectOptions[] = (
        searchedProductNatureData ?? []
      )
        .map((productNature) => {
          if ('externalReferential' in productNature) {
            return {
              label: `${productNature.externalReferential?.id} - ${productNature.externalReferential?.name}`,
              value: productNature.externalReferential?.id || -1,
              name: `${productNature.externalReferential?.name}`,
            };
          } else {
            if ('id' in productNature) {
              return {
                label: `${productNature.id} - ${productNature.englishName}`,
                value: productNature.id,
                name: `${productNature.englishName}`,
              };
            } else {
              // we should never land here but hey who am I to judge you ?
              return {
                label: '',
                value: -1,
                name: '',
              };
            }
          }
        })
        .filter((pno) => !productNaturesToHide.includes(pno.value));

      // eslint-disable-next-line react-hooks/set-state-in-effect
      setOptionsProductNaturesAndSelectedProductNatures(
        productNaturesListOptions,
      );
    }
  }, [searchedProductNatureData]);

  useEffect(() => {
    if (searchedProductNatureIsError) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setProductNaturesOptions([
        {
          label: t(`select.loading_error`) ?? '',
          value: NaN,
          name: '',
        },
      ]);
    }
  }, [searchedProductNatureIsError]);

  useEffect(() => {
    if (selectedProductNatureOptions.length > 0) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setOptionsProductNaturesAndSelectedProductNatures(
        selectedProductNatureOptions,
      );
    }
  }, []);

  const onInputChange = (search: string, actionMeta: InputActionMeta) => {
    if (actionMeta.action === metaActions.inputChange) {
      setSearchedProductNature(search);
    }
  };

  const handleChange = async (
    productNatures: mv<SelectOptions>,
    actionMeta: ActionMeta<SelectOptions>,
  ) => {
    const sortedProductNaturesOptions = sortOptions(
      productNatures,
      productNaturesOptions,
    );
    setProductNaturesOptions(sortedProductNaturesOptions);

    const allSelectedProductNatures = concatOptions(
      actionMeta,
      selectedProductNatures,
      productNatures,
    );

    onChange(allSelectedProductNatures);
    setSelectedProductNatures(allSelectedProductNatures);
  };

  return (
    <div className="vtmn-w-full" data-testid="product-natures_input_search">
      <span className="vtmn-typo_text-2">{t('input.nature_label')}</span>
      <Select
        data-testid="product-natures-search-input"
        aria-label={
          t(`select.product-natures_placeholder`) ||
          'Add one or several product natures'
        }
        className="vtmn-mt-2"
        styles={selectFilterStyles}
        placeholder={t(`select.product-natures_placeholder`)}
        isMulti
        value={selectedProductNatures}
        onChange={handleChange}
        onInputChange={onInputChange}
        isSearchable
        hideSelectedOptions={false}
        options={productNaturesOptions}
        isLoading={searchedProductNatureIsFetching}
        closeMenuOnSelect={false}
        components={{
          Option: IconOption,
          MultiValue,
        }}
        id="product-natures-filter"
        menuPlacement="top"
        menuPosition="fixed"
      />
    </div>
  );
};
